import { Controller } from "@hotwired/stimulus"

// Generic AI form field assistant
// Reads content from a source field and uses AI to populate a target field
//
// Usage:
//   <div data-controller="ai-form-assist"
//        data-ai-form-assist-url-value="/ai/form_assist"
//        data-ai-form-assist-field-type-value="meta_description"
//        data-ai-form-assist-source-value="#source_field_id"
//        data-ai-form-assist-target-value="#target_field_id">
//     <button type="button" data-action="click->ai-form-assist#generate">
//       Generate with AI
//     </button>
//   </div>
//
export default class extends Controller {
  static values = {
    url: String,
    fieldType: String,
    source: String,
    target: String
  }

  static targets = ["button", "spinner", "text", "icon"]

  async generate(event) {
    event.preventDefault()
    event.stopPropagation()

    const sourceElement = document.querySelector(this.sourceValue)
    const targetElement = document.querySelector(this.targetValue)

    if (!sourceElement) {
      console.error("[ai-form-assist] Source element not found:", this.sourceValue)
      return
    }

    if (!targetElement) {
      console.error("[ai-form-assist] Target element not found:", this.targetValue)
      return
    }

    const content = sourceElement.value?.trim()
    if (!content) {
      alert("Please add some content first before generating.")
      return
    }

    // Show loading state
    this.setLoading(true)

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
          "Accept": "application/json"
        },
        body: JSON.stringify({
          content: content,
          field_type: this.fieldTypeValue
        })
      })

      if (!response.ok) {
        if (response.status === 401) {
          alert("You must be logged in to use AI assistance.")
          return
        }
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()

      if (data.success && data.result) {
        targetElement.value = data.result
        // Trigger input event so any listeners (like character counters) update
        targetElement.dispatchEvent(new Event('input', { bubbles: true }))
      } else if (data.error) {
        alert(`Error: ${data.error}`)
      }
    } catch (error) {
      console.error("[ai-form-assist] Error:", error)
      alert("Failed to generate content. Please try again.")
    } finally {
      this.setLoading(false)
    }
  }

  setLoading(loading) {
    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = loading
    }
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.toggle("hidden", !loading)
    }
    if (this.hasIconTarget) {
      this.iconTarget.classList.toggle("hidden", loading)
    }
    if (this.hasTextTarget) {
      this.textTarget.textContent = loading ? "Generating..." : "Generate with AI"
    }
  }
}
